/* =========================================================
   SAFE UTILITIES
========================================================= */

function lockScroll() {
  document.body.classList.add("overflow-hidden");
}

function unlockScroll() {
  document.body.classList.remove("overflow-hidden");
  document.body.classList.remove("overflow-clip");
  document.documentElement.style.overflow = "";
}

function safeTrap(container, focusEl) {
  setTimeout(() => {
    if (typeof trapFocus === "function") {
      trapFocus(container, focusEl);
    }
  }, 50);
}

/* =========================================================
   MENU DRAWER
========================================================= */

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector("details");
  }

  open() {
    if (!this.details) return;

    this.details.setAttribute("open", "");
    this.classList.add("active");

    lockScroll();
    safeTrap(this, this.querySelector("summary"));
  }

  close() {
    if (!this.details) return;

    this.details.removeAttribute("open");
    this.classList.remove("active");

    if (typeof removeTrapFocus === "function") removeTrapFocus();
    unlockScroll();
  }
}

customElements.define("menu-drawer", MenuDrawer);

/* =========================================================
   HEADER DRAWER
========================================================= */

class HeaderDrawer extends MenuDrawer {
  open() {
    super.open();
    document.body.classList.add("drawer-open");
  }

  close() {
    super.close();
    document.body.classList.remove("drawer-open");
  }
}

customElements.define("header-drawer", HeaderDrawer);

/* =========================================================
   MODAL SYSTEM (SIZE GUIDE / POLICY / SEARCH FIX)
========================================================= */

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (e) => {
      if (e.target === this) this.hide();
    });

    this.addEventListener("keyup", (e) => {
      if (e.code === "Escape") this.hide();
    });
  }

  show(trigger) {
    this.openedBy = trigger;

    this.setAttribute("open", "");
    lockScroll();

    safeTrap(this, this.querySelector('[role="dialog"]'));
  }

  hide() {
    this.removeAttribute("open");
    this.classList.add("hiding");

    unlockScroll();

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.openedBy);
    }

    setTimeout(() => this.classList.remove("hiding"), 200);
  }
}

customElements.define("modal-dialog", ModalDialog);

/* =========================================================
   VARIANT SYSTEM (FIXED — NO WRONG CART ITEM)
========================================================= */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.onChange.bind(this));
  }

  onChange() {
    this.updateOptions();
    this.updateVariant();

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.updateInput();
    this.updateURL();
    this.updateMedia();
  }

  updateOptions() {
    const selects = [...this.querySelectorAll("select")].map(s => s.value);
    const radios = [...this.querySelectorAll("input:checked")].map(i => i.value);

    this.options = [...selects, ...radios];
  }

  updateVariant() {
    const data = this.getData();

    this.currentVariant = data.find(v =>
      v.options.every((opt, i) => opt === this.options[i])
    );
  }

  getData() {
    return JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
  }

  updateInput() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
      const input = form.querySelector('input[name="id"]');
      if (input && this.currentVariant) {
        input.value = this.currentVariant.id;
      }
    });
  }

  updateURL() {
    if (!this.currentVariant) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", this.currentVariant.id);
    window.history.replaceState({}, "", url);
  }

  updateMedia() {
    const mediaId = this.currentVariant?.featured_media?.id;
    if (!mediaId) return;

    const media = document.querySelector(`[data-media-id*="${mediaId}"]`);

    if (media) {
      media.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    setTimeout(() => {
      window.dispatchEvent(new Event("resize")); // swiper fix
    }, 60);
  }

  setUnavailable() {
    const btn = document.querySelector('[name="add"]');
    if (btn) btn.disabled = true;
  }
}

customElements.define("variant-selects", VariantSelects);

/* =========================================================
   VARIANT RADIOS FIX
========================================================= */

class VariantRadios extends VariantSelects {
  updateOptions() {
    const fieldsets = [...this.querySelectorAll("fieldset")];

    this.options = fieldsets.map(fs => {
      const checked = fs.querySelector("input:checked");
      return checked ? checked.value : null;
    });

    if (this.options.includes(null)) return;
  }
}

customElements.define("variant-radios", VariantRadios);

/* =========================================================
   CART VARIANT FIX (CRITICAL)
========================================================= */

document.addEventListener("click", (e) => {
  const size = e.target.closest(".js-size-variant");
  if (!size) return;

  const wrapper = size.closest(".card-wrapper");
  if (!wrapper) return;

  const form = wrapper.querySelector('form[action*="/cart/add"]');
  if (!form) return;

  const input = form.querySelector('input[name="id"]');
  if (!input) return;

  input.value = size.dataset.variantId;

  form.dispatchEvent(new Event("change", { bubbles: true }));
});

/* =========================================================
   ZOOM FIX (PRODUCT IMAGE)
========================================================= */

document.addEventListener("click", (e) => {
  const media = e.target.closest("[data-media-id]");
  if (!media) return;

  const img = media.querySelector("img") || media;

  if (img) {
    img.classList.toggle("zoom");
    document.body.classList.add("zoom-open");
  }
});

document.addEventListener("keydown", (e) => {
  if (e.code !== "Escape") return;

  document.body.classList.remove("zoom-open");

  document.querySelectorAll(".zoom").forEach(el => {
    el.classList.remove("zoom");
  });
});

/* =========================================================
   MODAL TRIGGERS (SIZE GUIDE / POLICY FIX)
========================================================= */

document.addEventListener("click", (e) => {
  const trigger = e.target.closest(
    "[data-modal], .js-size-guide, .js-policy, .product-popup-trigger"
  );

  if (!trigger) return;

  const selector = trigger.getAttribute("data-modal") || trigger.getAttribute("href");
  if (!selector) return;

  const modal = document.querySelector(selector);
  if (!modal) return;

  e.preventDefault();

  if (typeof modal.show === "function") {
    modal.show(trigger);
  } else {
    modal.setAttribute("open", "");
    document.body.classList.add("overflow-hidden");
  }
});

/* =========================================================
   GLOBAL SAFETY (SECTION RELOAD FIX)
========================================================= */

document.addEventListener("shopify:section:load", () => {
  unlockScroll();
});
document.addEventListener("click", (e) => {
  const openBtn = e.target.closest("[data-menu-open]");
  const closeBtn = e.target.closest("[data-menu-close]");
  const drawer = document.querySelector("header-drawer");

  if (!drawer) return;

  if (openBtn) {
    drawer.open();
  }

  if (closeBtn) {
    drawer.close();
  }
});
